import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp,
    FiRefreshCw, FiShield, FiAlertTriangle,
    FiLock, FiUsers, FiSearch, FiDownload, FiFilter, FiMoreVertical, FiExternalLink
} from 'react-icons/fi';
import { apiFetch, downloadCsv } from '../utils/api';
import UserDetailDrawer from '../components/UserDetailDrawer';

const PLAN_COLORS = {
    free:       'bg-gray-700 text-gray-300',
    starter:    'bg-blue-900/50 text-blue-300',
    team:       'bg-purple-900/50 text-purple-300',
    business:   'bg-amber-900/50 text-amber-300',
    enterprise: 'bg-emerald-900/50 text-emerald-300',
};

const RoleBadge = ({ role }) => {
    const c = role === 'owner' ? 'bg-amber-900/50 text-amber-300' :
              role === 'admin' ? 'bg-blue-900/50 text-blue-300' :
              'bg-gray-700 text-gray-400';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c}`}>{role}</span>;
};

const Users = () => {
    const [data,         setData]         = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [page,         setPage]         = useState(1);
    const [expanded,     setExpanded]     = useState(null);
    const [detailUserId, setDetailUserId] = useState(null);
    const [search,       setSearch]       = useState('');
    const [provider,     setProvider]     = useState('');
    const [exporting,    setExporting]    = useState(false);
    const [menuOpen,     setMenuOpen]     = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 20 });
            if (search.trim())  params.set('search', search.trim());
            const d = await apiFetch(`/superadmin/users?${params}`);
            // Filtre provider côté client (pas de param backend pour ça)
            setData(provider
                ? { ...d, users: d.users.filter(u => u.authProvider === provider) }
                : d);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [page, search, provider]);

    useEffect(() => { load(); }, [load]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set('search', search.trim());
            await downloadCsv(`/superadmin/export/users?${params}`, 'utilisateurs.csv');
        } catch (e) { console.error(e); }
        finally { setExporting(false); }
    };

    return (
        <>
        <div className="p-4 sm:p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-y-3">
                <div>
                    <h1 className="text-2xl font-bold text-white">Utilisateurs</h1>
                    {data && <p className="text-gray-500 text-sm mt-0.5">{data.total} utilisateur{data.total > 1 ? 's' : ''} au total</p>}
                </div>
                <div className="relative" ref={menuRef}>
                    <button
                        onClick={() => setMenuOpen(o => !o)}
                        className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white transition-colors"
                        title="Options"
                    >
                        <FiMoreVertical size={16} />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 z-20 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden min-w-[160px]">
                            <button onClick={() => { handleExport(); setMenuOpen(false); }} disabled={exporting}
                                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50">
                                <FiDownload size={13} className={exporting ? 'animate-bounce' : ''} />
                                Exporter CSV
                            </button>
                            <button onClick={() => { load(); setMenuOpen(false); }} disabled={loading}
                                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50">
                                <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                                Actualiser
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Filtres */}
            <div className="flex flex-wrap gap-3 items-center bg-gray-900 border border-gray-800 rounded-xl p-3">
                <FiFilter size={14} className="text-gray-500" />
                <div className="relative">
                    <FiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Nom ou email..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                        className="bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-gray-300 text-sm focus:outline-none focus:border-brand-500 w-44"
                    />
                </div>
                <select value={provider} onChange={e => { setProvider(e.target.value); setPage(1); }}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-300 text-sm focus:outline-none focus:border-brand-500">
                    <option value="">Tous les providers</option>
                    <option value="local">local</option>
                    <option value="google">google</option>
                </select>
                {data && <span className="text-gray-500 text-sm ml-auto">{data.total} utilisateur{data.total > 1 ? 's' : ''}</span>}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : (
                <div className="space-y-2">
                    {data?.users?.map(u => (
                        <div key={u._id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                            <div
                                className="flex items-center gap-4 px-4 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors"
                                onClick={() => setExpanded(expanded === u._id ? null : u._id)}
                            >
                                {/* Avatar */}
                                <div className="relative w-9 h-9 rounded-full bg-brand-600/20 flex items-center justify-center text-brand-400 font-bold shrink-0">
                                    {(u.name || '?')[0].toUpperCase()}
                                    {u.twoFactorEnabled && (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-gray-900 flex items-center justify-center">
                                            <FiLock size={8} className="text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Infos */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={e => { e.stopPropagation(); setDetailUserId(u._id); }}
                                            className="text-white font-semibold hover:text-brand-400 transition-colors flex items-center gap-1"
                                            title="Voir le détail"
                                        >
                                            {u.name}
                                            <FiExternalLink size={12} className="text-gray-600 hover:text-brand-400" />
                                        </button>
                                        {u.isSuperAdmin && (
                                            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-brand-900/50 text-brand-300 flex items-center gap-1">
                                                <FiShield size={10} /> Super Admin
                                            </span>
                                        )}
                                        {!u.isEmailVerified && (
                                            <FiAlertTriangle size={13} className="text-amber-400" title="Email non vérifié" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                                        <span className="text-gray-400 text-xs truncate">{u.email}</span>
                                        <span className="text-gray-600 text-xs flex items-center gap-1">
                                            <FiUsers size={10} /> {u.tenants?.length ?? 0} org{u.tenants?.length > 1 ? 's' : ''}
                                        </span>
                                        <span className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                                            u.authProvider === 'google' ? 'bg-blue-900/40 text-blue-400' : 'bg-gray-800 text-gray-400'
                                        }`}>{u.authProvider}</span>
                                    </div>
                                </div>

                                <div className="text-gray-500 text-xs shrink-0 text-right">
                                    {new Date(u.createdAt).toLocaleDateString('fr-FR')}
                                </div>
                                {expanded === u._id
                                    ? <FiChevronUp size={16} className="text-gray-500 shrink-0" />
                                    : <FiChevronDown size={16} className="text-gray-500 shrink-0" />
                                }
                            </div>

                            {/* Détails */}
                            {expanded === u._id && (
                                <div className="border-t border-gray-800 p-4 bg-gray-950/50 space-y-4">
                                    {/* Meta */}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {[
                                            ['ID', u._id],
                                            ['Email vérifié', u.isEmailVerified ? 'Oui' : 'Non'],
                                            ['2FA', u.twoFactorEnabled ? 'Activé' : 'Désactivé'],
                                            ['Tentatives login', u.loginAttempts ?? 0],
                                            ['Inscrit le', new Date(u.createdAt).toLocaleDateString('fr-FR')],
                                            ['Auth', u.authProvider],
                                            ['Thème', u.theme],
                                            ['Notifs email', u.emailNotifications ? 'Oui' : 'Non'],
                                        ].map(([k, v]) => (
                                            <div key={k} className="bg-gray-900 rounded-lg p-3">
                                                <div className="text-gray-500 text-xs mb-1">{k}</div>
                                                <div className="text-white font-medium font-mono text-xs break-all">{String(v)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Organisations */}
                                    {u.tenants?.length > 0 && (
                                        <div>
                                            <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                                                Organisations ({u.tenants.length})
                                            </h4>
                                            <div className="bg-gray-900 rounded-xl overflow-hidden">
                                                {u.tenants.map((t, i) => (
                                                    <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-gray-800' : ''}`}>
                                                        <div className="min-w-0">
                                                            <span className="text-white text-sm font-medium">{t.tenantName || t.tenantId}</span>
                                                            {t.tenantStatus === 'suspended' && (
                                                                <span className="ml-2 text-xs text-red-400">(suspendu)</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            {t.tenantPlan && (
                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PLAN_COLORS[t.tenantPlan] || 'bg-gray-700 text-gray-300'}`}>
                                                                    {t.tenantPlan}
                                                                </span>
                                                            )}
                                                            <RoleBadge role={t.role} />
                                                            <span className="text-gray-600 text-xs">
                                                                {t.joinedAt ? new Date(t.joinedAt).toLocaleDateString('fr-FR') : ''}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Alerte brute force */}
                                    {(u.loginAttempts ?? 0) >= 3 && (
                                        <div className="flex items-center gap-2 bg-amber-900/20 border border-amber-800/50 rounded-lg px-3 py-2 text-amber-300 text-sm">
                                            <FiAlertTriangle size={14} />
                                            {(u.loginAttempts ?? 0) >= 5
                                                ? `Compte verrouillé (${u.loginAttempts} tentatives)`
                                                : `${u.loginAttempts} tentatives de connexion échouées`
                                            }
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {data && data.pages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-2">
                    <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
                        className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 transition-colors">
                        <FiChevronLeft size={16} />
                    </button>
                    <span className="text-gray-400 text-sm">{page} / {data.pages}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={page >= data.pages}
                        className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 transition-colors">
                        <FiChevronRight size={16} />
                    </button>
                </div>
            )}

        </div>

        {detailUserId && (
            <UserDetailDrawer
                userId={detailUserId}
                onClose={() => setDetailUserId(null)}
                onUpdate={() => load()}
            />
        )}
        </>
    );
};

export default Users;
